// components/layout/dropdowns/MessagesMenu.jsx
import React, { useState, useMemo, useCallback } from 'react';
import { 
  Menu,
  Box,
  Badge,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Button,
  CircularProgress,
  Alert} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  MailOutline as MailOutlineIcon,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { axiosService } from 'shared/helpers/axios';
import { useUser } from "shared/helpers/axios";
import MessageNotificationCard from './MessageNotificationCard';

const MessagesMenu = ({ 
  messages = [], 
  messageAnchorEl, 
  isMessageMenuOpen, 
  unreadCount = 0,
  onClose,
  onMessageClick,
  onMarkAllAsRead,
  onNewMessage,
  messageMenuId = 'message-menu'
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useUser();
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get messages list
  const getMessagesList = useCallback(() => {
    if (!messages) return [];
    
    if (Array.isArray(messages)) {
      return messages;
    }
    
    if (messages.results && Array.isArray(messages.results)) {
      return messages.results;
    }
    
    return [];
  }, [messages]);

  const messagesList = getMessagesList();

  // Filter messages for current user
  const currentUserMessages = useMemo(() => {
    if (!currentUser || !currentUser.id) return messagesList;
    
    // console.log("***** ALL MESSAGES", messagesList)

    return messagesList.filter(message => {
      // New format: check sender_actor and recipient_actor
      if (message.sender_actor || message.recipient_actor) {
        return true; // All conversations are relevant
      }
      
      // Old format fallback
      const recipients = message.recipients || [];
      const isRecipient = recipients.some(recipient => 
        recipient.id === currentUser.id || recipient.user_id === currentUser.id
      );
      
      if (message.recipient_id) {
        return message.recipient_id === currentUser.id;
      }
      
      return isRecipient || (message.partner && message.partner.id !== currentUser.id);
    });
  }, [messagesList, currentUser]);

  // Sort messages by date (newest first)
  const sortedMessages = useMemo(
    () => [...currentUserMessages].sort((a, b) => {
      // Each item is a conversation with last_message_at
      const timeA = new Date(a.last_message_at || a.last_message?.created || a.created_at || a.timestamp || a.created);
      const timeB = new Date(b.last_message_at || b.last_message?.created || b.created_at || b.timestamp || b.created);
      return timeB - timeA;
    }),
    [currentUserMessages]
  );

  // Categorize messages
  const categorizedMessages = useMemo(() => {
    const directMessages = [];
    const groupMessages = [];
    const unreadMessages = [];
    const starredMessages = [];
    
    sortedMessages.forEach(conversation => {
      // Each item is a conversation with last_message
      const isGroup = conversation.conversation_type === 'user_to_consortium' || conversation.recipients?.length > 2 || conversation.is_group_message;
      const isUnread = conversation.unread_count > 0 || (!conversation.last_message?.is_read && !conversation.last_message?.read);
      const isStarred = conversation.last_message?.is_starred_by_sender || conversation.last_message?.is_starred_by_recipient;
      
      if (isGroup) {
        groupMessages.push(conversation);
      } else {
        directMessages.push(conversation);
      }
      
      if (isUnread) {
        unreadMessages.push(conversation);
      }
      
      if (isStarred) {
        starredMessages.push(conversation);
      }
    });
    
    return { directMessages, groupMessages, unreadMessages, starredMessages };
  }, [sortedMessages]);

  // Search filter
  const matchesSearch = useCallback((message) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    
    // Get partner info from new format
    let partnerName = '';
    if (message.conversation_type === 'user_to_consortium') {
      // For consortium conversations, search in consortium name
      const consortiumActor = (message.recipient_actor?.actor_type === 'consortium' || message.recipient_actor?.actor_type === 'organization')
        ? message.recipient_actor
        : message.sender_actor;
      partnerName = consortiumActor?.display_name || '';
    } else if (message.sender_actor && message.recipient_actor) {
      // For user conversations, search in the other user's name
      const otherActor = message.sender_actor.entity_id === currentUser.id ? message.recipient_actor : message.sender_actor;
      partnerName = otherActor?.display_name || '';
    }
    
    // Fallback to old format
    const partner = message.partner || message.sender_info || {};
    const participants = message.participants || [];
    
    const searchableText = [
      partnerName,
      partner.name,
      partner.username,
      partner.email,
      message.body,
      message.content,
      message.preview,
      message.last_message?.body_preview,
      ...participants.map(p => p.name || p.username || '')
    ].filter(Boolean).join(' ').toLowerCase();
    
    return searchableText.includes(query);
  }, [searchQuery, currentUser]);

  // Active filter function
  const getActiveFilter = useCallback(() => {
    switch (activeFilter) {
      case 'unread':
        return (conversation) => conversation.unread_count > 0 || (!conversation.last_message?.is_read);
      case 'group':
        return (conversation) => conversation.conversation_type === 'user_to_consortium' || conversation.recipients?.length > 2 || conversation.is_group_message;
      case 'direct':
        return (conversation) => conversation.conversation_type !== 'user_to_consortium' && !(conversation.recipients?.length > 2) && !conversation.is_group_message;
      case 'starred':
        return (conversation) => conversation.last_message?.is_starred_by_sender || conversation.last_message?.is_starred_by_recipient;
      default:
        return () => true;
    }
  }, [activeFilter]);

  // Filtered messages
  const filteredMessages = useMemo(() => {
    const filterFunc = getActiveFilter();
    return sortedMessages.filter(msg => filterFunc(msg) && matchesSearch(msg));
  }, [sortedMessages, getActiveFilter, matchesSearch]);

  // Unread count in current view
  const unreadInView = useMemo(() => 
    filteredMessages.filter(conv => conv.unread_count > 0 || (!conv.last_message?.is_read && !conv.last_message?.read)).length,
    [filteredMessages]
  );

  // Mark all visible as read
  const markAllVisibleAsRead = useCallback(async () => {
    const unreadConversations = filteredMessages.filter(conv => conv.unread_count > 0);
    
    for (const conversation of unreadConversations) {
      try {
        // Determine partner actor and build request payload
        let partnerType, partnerId, payload;
        if (conversation.conversation_type === 'user_to_consortium') {
          const consortiumActor = (conversation.recipient_actor?.actor_type === 'consortium' || conversation.recipient_actor?.actor_type === 'organization')
            ? conversation.recipient_actor
            : conversation.sender_actor;
          partnerType = consortiumActor.actor_type;
          partnerId = consortiumActor.entity_id;
          
          // For consortium conversations, also pass actor IDs to mark specific thread
          payload = {
            partner_type: partnerType,
            partner_id: partnerId,
            sender_actor_id: conversation.sender_actor?.id,
            recipient_actor_id: conversation.recipient_actor?.id
          };
        } else {
          const otherActor = conversation.sender_actor?.entity_id === currentUser.id ? conversation.recipient_actor : conversation.sender_actor;
          partnerType = otherActor.actor_type;
          partnerId = otherActor.entity_id;
          payload = {
            partner_type: partnerType,
            partner_id: partnerId
          };
        }
        
        await axiosService.post('/interact/messages/mark-conversation-read/', payload);
      } catch (error) {
        console.error('Error marking conversation as read:', error);
      }
    }
    
    // Trigger refresh of conversations
    if (unreadConversations.length > 0) {
      window.dispatchEvent(new CustomEvent('messagesPageOpened'));
    }
    
    if (onMarkAllAsRead && unreadConversations.length > 0) {
      onMarkAllAsRead();
    }
  }, [filteredMessages, onMarkAllAsRead, currentUser]);

  const handleMessageClick = async (conversation) => {
    
    // Navigate to message thread
    if (conversation.sender_actor || conversation.recipient_actor) {
      // New actor-based format
      if (conversation.conversation_type === 'user_to_consortium') {
        // For consortium conversations, pass both actor IDs to uniquely identify the thread
        navigate(`/messages?sender=${conversation.sender_actor.id}&recipient=${conversation.recipient_actor.id}`);
      } else {
        // For user-to-user conversations, pass the other user's entity_id
        const targetActor = conversation.sender_actor?.entity_id === currentUser.id 
          ? conversation.recipient_actor 
          : conversation.sender_actor;
        navigate(`/messages/?user=${targetActor.entity_id}`);
      }
    } else if (conversation.conversation_id) {
      navigate(`/messages/?user=${conversation.conversation_id}`);
    } else if (conversation.partner?.id) {
      navigate(`/messages/?user=${conversation.partner.id}`);
    } else {
      navigate('/messages/');
    }

    handleClose();

    // Mark conversation as read if it has unread messages
    const isUnread = conversation.unread_count > 0 || !conversation.last_message?.is_read;
    if (isUnread) {
      try {
        // Determine partner actor and build request payload
        let partnerType, partnerId, payload;
        if (conversation.conversation_type === 'user_to_consortium') {
          const consortiumActor = (conversation.recipient_actor?.actor_type === 'consortium' || conversation.recipient_actor?.actor_type === 'organization')
            ? conversation.recipient_actor
            : conversation.sender_actor;
          partnerType = consortiumActor.actor_type;
          partnerId = consortiumActor.entity_id;
          
          // For consortium conversations, also pass actor IDs to mark specific thread
          payload = {
            partner_type: partnerType,
            partner_id: partnerId,
            sender_actor_id: conversation.sender_actor?.id,
            recipient_actor_id: conversation.recipient_actor?.id
          };
        } else {
          const otherActor = conversation.sender_actor?.entity_id === currentUser.id ? conversation.recipient_actor : conversation.sender_actor;
          partnerType = otherActor.actor_type;
          partnerId = otherActor.entity_id;
          payload = {
            partner_type: partnerType,
            partner_id: partnerId
          };
        }
        
        await axiosService.post('/interact/messages/mark-conversation-read/', payload);
        window.dispatchEvent(new CustomEvent('messagesPageOpened'));
      } catch (error) {
        console.error('Error marking conversation as read:', error);
      }
    }
  };

  const handleMarkAsRead = async (conversation) => {
    try {
      // Determine partner actor and build request payload
      let partnerType, partnerId, payload;
      if (conversation.conversation_type === 'user_to_consortium') {
        const consortiumActor = (conversation.recipient_actor?.actor_type === 'consortium' || conversation.recipient_actor?.actor_type === 'organization')
          ? conversation.recipient_actor
          : conversation.sender_actor;
        partnerType = consortiumActor.actor_type;
        partnerId = consortiumActor.entity_id;
        
        // For consortium conversations, also pass actor IDs to mark specific thread
        payload = {
          partner_type: partnerType,
          partner_id: partnerId,
          sender_actor_id: conversation.sender_actor?.id,
          recipient_actor_id: conversation.recipient_actor?.id
        };
      } else {
        const otherActor = conversation.sender_actor?.entity_id === currentUser.id ? conversation.recipient_actor : conversation.sender_actor;
        partnerType = otherActor.actor_type;
        partnerId = otherActor.entity_id;
        payload = {
          partner_type: partnerType,
          partner_id: partnerId
        };
      }
      
      await axiosService.post('/interact/messages/mark-conversation-read/', payload);
      
      // Refresh conversations
      window.dispatchEvent(new CustomEvent('messagesPageOpened'));
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  const handleStar = async (conversation) => {
    try {
      const messageId = conversation.last_message?.id || conversation.id;
      await axiosService.post(`/interact/messages/${messageId}/toggle-star/`);
      // Refresh conversations
      window.dispatchEvent(new CustomEvent('messagesPageOpened'));
    } catch (error) {
      console.error('Error toggling star:', error);
    }
  };

  const handleArchive = async (conversation) => {
    try {
      const messageId = conversation.last_message?.id || conversation.id;
      await axiosService.post(`/interact/messages/${messageId}/archive/`);
      // Refresh conversations
      window.dispatchEvent(new CustomEvent('messagesPageOpened'));
    } catch (error) {
      console.error('Error archiving message:', error);
    }
  };

  const handleDelete = async (conversation) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    
    try {
      const messageId = conversation.last_message?.id || conversation.id;
      await axiosService.delete(`/interact/messages/${messageId}/`);
      // Refresh conversations
      window.dispatchEvent(new CustomEvent('messagesPageOpened'));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleReply = (conversation) => {
    // Navigate to messages page (it will show the conversation)
    navigate(`/messages/`);
    handleClose();
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      // Dispatch event to trigger refresh in parent
      window.dispatchEvent(new CustomEvent('messagesPageOpened'));
      setError(null);
    } catch (err) {
      setError('Failed to refresh messages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleViewAll = () => {
    navigate('/messages/');
    handleClose();
  };

  const handleNewMessage = () => {
    if (onNewMessage) {
      onNewMessage();
    } else {
      navigate('/messages/new/');
    }
    handleClose();
  };

  return (
    <Menu
      anchorEl={messageAnchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={messageMenuId}
      keepMounted
      open={isMessageMenuOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          mt: 1,
          minWidth: 420,
          maxWidth: 460,
          maxHeight: 600,
          overflow: 'hidden',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.16)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="h5" fontWeight={600}>
            Messages
            {unreadCount > 0 && (
              <Badge 
                badgeContent={unreadCount} 
                color="error" 
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton 
              size="small" 
              onClick={handleRefresh}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleClose}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Search Bar */}

       <Stack flexDirection={'row'}>
        <TextField
          sx={{ width: '70%', mt: 2 }} 
          size="small"
          placeholder="Search messages…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

{/* Action Buttons */}
      <Box sx={{ 
        // p: 1.5, 
        // borderBottom: 1, 
        // borderColor: 'divider',
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        ml: 1.5,
        mt:2,
        gap: 1,
        bgcolor: 'background.default'
      }}>
        {/* <Button
          size="small"
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleNewMessage}
          sx={{ 
            fontSize: '0.75rem',
            flex: 1,
            textTransform: 'none'
          }}
        >
          New Message
        </Button> */}
        
        {unreadInView > 0 && (
          <Button
            size="small"
            // variant="outlined"
            // startIcon={<CheckCircleIcon />}
            onClick={markAllVisibleAsRead}
            // onClick={onMarkAllAsRead}
            sx={{ 
              fontSize: '0.95rem',
              textTransform: 'none'
            }}
          >
            Mark all read ({unreadInView})
          </Button>
        )}
      </Box>
      </Stack> 


        {/* Filter Chips */}
        {/* <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {filters.map((filter) => (
            <Chip
              key={filter.key}
              label={`${filter.label} ${filter.count > 0 ? `(${filter.count})` : ''}`}
              size="small"
              color={activeFilter === filter.key ? filter.color : "default"}
              variant={activeFilter === filter.key ? "filled" : "outlined"}
              onClick={() => handleFilterClick(filter.key)}
              sx={{ 
                fontSize: '0.75rem',
                height: 26
              }}
            />
          ))}
        </Box> */}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ mx: 2, mt: 1 }}
        >
          {error}
        </Alert>
      )}

    

      {/* Message Cards List */}
      <Box sx={{ 
        maxHeight: 320, 
        overflow: 'auto',
        p: 1.5
      }}>
        {filteredMessages.length === 0 ? (
          <Box sx={{ 
            py: 6, 
            textAlign: 'center', 
            color: 'text.secondary' 
          }}>
            <MailOutlineIcon sx={{ fontSize: 48, mb: 2, color: 'action.disabled' }} />
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              {searchQuery ? 'No matching messages' : 'No messages'}
            </Typography>
            <Typography variant="body2">
              {searchQuery 
                ? 'Try a different search term' 
                : activeFilter === 'unread' 
                  ? 'You have no unread messages'
                  : 'Start a conversation with someone!'}
            </Typography>
            {!searchQuery && activeFilter !== 'unread' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={handleNewMessage}
                sx={{ mt: 2 }}
              >
                Send your first message
              </Button>
            )}
          </Box>
        ) : (
          filteredMessages.map((message) => (
            <MessageNotificationCard
              key={message.id}
              message={message}
              currentUser={currentUser}
              onMessageClick={handleMessageClick}
              onMarkAsRead={handleMarkAsRead}
              onStar={handleStar}
              onArchive={handleArchive}
              onDelete={handleDelete}
              onReply={handleReply}
            />
          ))
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ 
        borderTop: 1, 
        borderColor: 'divider',
        p: 1.5
      }}>
        <Button
          fullWidth
          variant="text"
          size="small"
          onClick={handleViewAll}
          sx={{ 
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 600
          }}
        >
          View all messages
        </Button>
      </Box>
    </Menu>
  );
};

export default MessagesMenu;